import { banco } from '../db/banco'
import { novoRegistroBase } from '../db/tipos'
import type { ItemPedido, Pedido, Produto, SubPedido } from '../db/tipos'
import { gravarLocalEEnfileirar, apagarLocalEEnfileirar } from '../sync/outbox'
import { arredondar2, calcularTotalItem, avulsasDoItem, dataISOLocal } from './precos'
import type { Avulsas } from './precos'
import { embalagemDoProduto, unidadesTotais } from '../produtos/embalagem'
import { resolverPreco } from './resolverPreco'
import { comporImpostoInformativo } from './impostoInformativo'
import { proximoNumeroPedido, letraDoIndice, indiceDaLetra } from './numeracao'
import { sincronizarLancamento } from '../comissoes/lancamentos'
import {
  registrarAjuste, MOTIVO_CORRECAO, ehMotivoDeAcrescimo, ehMotivoDeDevolucao,
} from './ajustes'
// fechamento.ts nao importa carrinho.ts, entao nao ha ciclo de importacao aqui.
import { mudarStatusSubPedido } from './fechamento'

export async function obterOuCriarPedidoAberto(clienteId: string): Promise<Pedido> {
  // Transacao rw serializa chamadas concorrentes (ex.: duplo toque em "Novo pedido"):
  // a 2a chamada so roda apos a 1a commitar, entao enxerga o pedido ja criado.
  return banco.transaction('rw', [banco.pedidos, banco.outbox], async () => {
    const existente = await banco.pedidos
      .where('cliente_id')
      .equals(clienteId)
      .filter((p) => p.apagado_em === null && p.status === 'aberto')
      .first()
    if (existente) return existente
    const pedido: Pedido = {
      ...novoRegistroBase(),
      cliente_id: clienteId,
      numero: await proximoNumeroPedido(),
      data: dataISOLocal(new Date()),
      status: 'aberto',
      observacoes: null,
    }
    await gravarLocalEEnfileirar('pedidos', pedido)
    return pedido
  })
}

async function subPedidosAtivos(pedidoId: string): Promise<SubPedido[]> {
  return banco.sub_pedidos
    .where('pedido_id')
    .equals(pedidoId)
    .filter((s) => s.apagado_em === null)
    .sortBy('letra')
}

export async function itensAtivos(subPedidoId: string): Promise<ItemPedido[]> {
  return banco.itens_pedido
    .where('sub_pedido_id')
    .equals(subPedidoId)
    .filter((i) => i.apagado_em === null)
    .toArray()
}

// Proxima letra = maior letra ATIVA + 1 (nunca a contagem: apos remover um
// sub-pedido do meio, a contagem reutilizaria uma letra ainda ativa -> colisao
// no indice unico (pedido_id, letra) do servidor). Carrinho vazio recomeca em A.
function proximaLetraAtiva(ativos: SubPedido[]): string {
  return ativos.length === 0
    ? letraDoIndice(0)
    : letraDoIndice(Math.max(...ativos.map((s) => indiceDaLetra(s.letra))) + 1)
}

async function criarSubPedido(
  pedidoId: string,
  representadaId: string,
  tabelaPrecoId: string | null,
  ativos: SubPedido[],
  freteTipo: SubPedido['frete_tipo'] = null,
  condicaoPagamentoId: string | null = null,
): Promise<SubPedido> {
  const subPedido: SubPedido = {
    ...novoRegistroBase(),
    pedido_id: pedidoId,
    representada_id: representadaId,
    letra: proximaLetraAtiva(ativos),
    status: 'rascunho',
    motivo_cancelamento: null,
    tabela_preco_id: tabelaPrecoId,
    condicao_pagamento_id: condicaoPagamentoId,
    frete_tipo: freteTipo,
    transportadora: null,
    previsao_entrega: null,
    valor_total: 0,
    valor_faturado: null,
    pdf_url: null,
    observacoes: null,
  }
  await gravarLocalEEnfileirar('sub_pedidos', subPedido)
  return subPedido
}

export async function recalcularTotais(pedidoId: string): Promise<void> {
  for (const subPedido of await subPedidosAtivos(pedidoId)) {
    const itens = await itensAtivos(subPedido.id)
    const total = itens.reduce((soma, item) => soma + item.total_item, 0)
    const arredondado = arredondar2(total)
    if (arredondado !== subPedido.valor_total) {
      await gravarLocalEEnfileirar('sub_pedidos', { ...subPedido, valor_total: arredondado })
    }
  }
}

// Ate 2026-07-28 os itens eram imutaveis a partir do fechamento. O Ciclo 1
// abriu ate ENVIADO: o vendedor precisa refletir o corte da fabrica antes de
// faturar. A partir de faturado congela -- e a guarda mora aqui, na camada que
// grava, e nao so na tela: tela some por engano, regra escrita nao.
const STATUS_EDITAVEL: ReadonlyArray<SubPedido['status']> = ['rascunho', 'fechado', 'enviado']

function garantirEditavel(subPedido: SubPedido): void {
  if (!STATUS_EDITAVEL.includes(subPedido.status)) {
    throw new Error(
      `Sub-pedido ${subPedido.letra} está "${subPedido.status}": itens congelados a partir do faturamento.`,
    )
  }
}

export async function adicionarItem(
  pedidoId: string,
  clienteId: string,
  produto: Produto,
  tabelaId: string,
  quantidade: number,
  // As unidades SOLTAS do mesmo produto, na mesma linha. O default zerado e o
  // que faz as chamadas antigas continuarem gravando exatamente como gravavam.
  avulsas: Avulsas = { quantidade: 0, preco: 0 },
  motivo?: string,
  // Novos: so usados quando a representada tem calcula_imposto = true (Tela
  // nova, Task 5, pergunta os dois ANTES de abrir o carrinho dela).
  freteTipo: SubPedido['frete_tipo'] = null,
  condicaoPagamentoId: string | null = null,
): Promise<{ sucesso: true } | { sucesso: false; motivo: 'sem_preco' }> {
  // ⛔ A trava que o banco tem desde a 0021 e o Dexie NAO tinha. Sem ela o app
  // gravava local, mostrava um total errado e so o Postgres recusava o push --
  // deixando a mutacao envenenada presa na fila. A guarda mora na camada que
  // GRAVA, e nao so na tela: tela some por engano, regra escrita nao.
  if (avulsas.quantidade > 0 && !(avulsas.preco > 0)) {
    throw new Error(
      'Unidade avulsa sem preço: esta tabela não vende a unidade solta deste produto.',
    )
  }
  const ativos = await subPedidosAtivos(pedidoId)
  // Sub-pedido agora e por marca + tabela (mesma marca pode ter 2 tabelas).
  let subPedido = ativos.find(
    (s) => s.representada_id === produto.representada_id && s.tabela_preco_id === tabelaId,
  )
  if (subPedido) garantirEditavel(subPedido)

  // Ate 2026-07-29 acrescentar depois do envio era PROIBIDO. Caiu porque o smoke
  // dela achou o beco sem saida: o aviso de pedido minimo aparecia e nao havia
  // como completar o valor. A fronteira que a proibicao protegia ("refletir o
  // corte da fabrica" x "vender mais") passou a ser guardada pelo MOTIVO, que e
  // de lista propria e nao conta como venda perdida.
  const comprometido = subPedido !== undefined && subPedido.status !== 'rascunho'
  if (comprometido) {
    if (!motivo) throw new Error('Informe o motivo do acréscimo.')
    if (!ehMotivoDeAcrescimo(motivo)) {
      throw new Error(
        'Motivo de corte não serve para acréscimo: use "Completar o mínimo da fábrica", ' +
          '"Cliente pediu mais" ou "Correção minha".',
      )
    }
  }

  // A tabela ja vem escolhida (a linha da busca ja e um preco). So resolve o
  // preco: negociado (cliente+produto) sobrepoe o preco da tabela.
  const preco = await resolverPreco(produto.id, clienteId, tabelaId)
  if (!preco) return { sucesso: false, motivo: 'sem_preco' }

  if (!subPedido) {
    subPedido = await criarSubPedido(
      pedidoId, produto.representada_id, tabelaId, ativos, freteTipo, condicaoPagamentoId,
    )
  }

  // NOVO: tenta calcular o imposto. Le a condicao de pagamento (para o
  // desconto_pct dela) e o cliente (para o CEP) so quando a representada
  // calcula imposto -- senao e trabalho a toa em todo pedido de toda
  // representada comum.
  // ⚠⚠ MUDOU EM 14/10/2026 (palavra dela): o valor de VENDA e o preco da
  // tabela -- e ele que vai para preco_unitario, total, comissao e meta. O
  // imposto calculado aqui e INFORMATIVO: mostra ao cliente quanto ele vai
  // pagar de IPI e ICMS ST nesta compra, e fica congelado nos campos
  // `imposto_*` do item so para o PDF. A regra de 09/09 ("composicao.precoFinal
  // e sempre o preco cobrado") foi substituida; nao a restaure sem ela pedir.
  const precoCobrado = preco.preco
  const composicao = await comporImpostoInformativo(
    produto, clienteId, preco.preco, freteTipo, condicaoPagamentoId,
  )

  const existente = (await itensAtivos(subPedido.id)).find((i) => i.produto_id === produto.id)
  if (existente) {
    const novaQuantidade = existente.quantidade + quantidade
    // Somar o mesmo produto soma as DUAS dimensoes -- "1 cx" mais "6 un" vira
    // uma linha so com as duas, e nao uma linha que perde a metade solta.
    const jaTinha = avulsasDoItem(existente)
    const novaAvulsa = jaTinha.quantidade + avulsas.quantidade
    // O preco da unidade solta vem da linha de preco de HOJE; o que o item ja
    // tinha so prevalece quando a chamada nova nao traz nenhum.
    const precoAvulso = avulsas.preco > 0 ? avulsas.preco : jaTinha.preco
    const atualizado = {
      ...existente,
      quantidade: novaQuantidade,
      quantidade_avulsa: novaAvulsa,
      preco_avulso: precoAvulso > 0 ? precoAvulso : null,
      total_item: calcularTotalItem(
        novaQuantidade, existente.preco_unitario, existente.desconto_pct,
        { quantidade: novaAvulsa, preco: precoAvulso },
      ),
    }
    await gravarLocalEEnfileirar('itens_pedido', atualizado)
    if (comprometido && motivo) {
      await registrarAjuste({
        sub_pedido_id: subPedido.id,
        escopo: 'item',
        item_pedido_id: existente.id,
        produto_id: produto.id,
        quantidade_antes: existente.quantidade,
        quantidade_depois: novaQuantidade,
        valor_antes: existente.total_item,
        valor_depois: atualizado.total_item,
        motivo,
      })
    }
  } else {
    const novo: ItemPedido = {
      ...novoRegistroBase(),
      sub_pedido_id: subPedido.id,
      produto_id: produto.id,
      quantidade,
      quantidade_avulsa: avulsas.quantidade,
      // Guarda o preco mesmo com quantidade zero: e o preco da linha de hoje, e
      // e ele que a tela mostra ao lado do campo ainda vazio. Nulo so quando
      // nao ha preco nenhum -- o caso de todo produto sem embalagem fechada.
      preco_avulso: avulsas.preco > 0 ? avulsas.preco : null,
      // ⚠ CONGELA o fator no momento da venda. Se a fabrica trocar a caixa de
      // 20 para 24, o PDF de um pedido antigo nao pode se reescrever sozinho --
      // a spec e explicita, e embalagemDoItem() do outro lado conta com isto.
      unidades_por_embalagem: embalagemDoProduto(produto).fator,
      preco_unitario: precoCobrado,
      desconto_pct: 0,
      origem_preco: preco.origem,
      total_item: calcularTotalItem(quantidade, precoCobrado, 0, avulsas),
      // Composicao do imposto (motor 0024), congelada no momento do calculo.
      // Nulos quando o item nao passou pelo motor.
      imposto_origem: composicao?.origem ?? null,
      imposto_destino_icms: composicao?.destinoIcms ?? null,
      imposto_diferenca_icms: composicao?.diferencaIcms ?? null,
      imposto_ipi: composicao?.ipi ?? null,
    }
    await gravarLocalEEnfileirar('itens_pedido', novo)
    if (comprometido && motivo) {
      // Item que nao existia: "antes" e zero de propósito -- e o que faz a linha
      // do historico se ler como acrescimo, e nao como mudanca de quantidade.
      await registrarAjuste({
        sub_pedido_id: subPedido.id,
        escopo: 'item',
        item_pedido_id: novo.id,
        produto_id: produto.id,
        quantidade_antes: 0,
        quantidade_depois: quantidade,
        valor_antes: 0,
        valor_depois: novo.total_item,
        motivo,
      })
    }
  }
  await recalcularTotais(pedidoId)
  // A comissao acompanha o item real. Em rascunho ainda nao ha lancamento.
  if (comprometido) await sincronizarLancamento(subPedido.id)
  return { sucesso: true }
}

export type AcaoQuantidade =
  | { acao: 'remover' }
  | { acao: 'quantidade'; nova: number }
  | { acao: 'recusar'; aviso: string }

// O que o vendedor quis dizer ao digitar na caixinha de quantidade.
// ZERO quer dizer "tira isso" -- e o gesto natural, e antes deste conserto a
// tela apenas devolvia em silencio (nao pedia motivo, nao recalculava, nao
// avisava). Nenhum caminho daqui devolve em silencio: recusa sempre tem aviso.
export function interpretarQuantidade(
  texto: string,
  quantidadeAtual: number,
  // ▶ A OUTRA dimensao da mesma linha. Depois da 0021 "0 caixas + 6 unidades" e
  // uma venda inteira, e ler o zero como "tira isso" deixaria essa venda
  // existir no banco e nao existir no dedo dela. Zero por omissao: a tela de
  // Ajustar itens chama sem este parametro e nada muda por la, onde o zero
  // APAGA o item de proposito.
  outraDimensao = 0,
): AcaoQuantidade {
  // A virgula decimal e o separador do teclado dela; o projeto ja perdeu preco
  // em silencio por causa disso.
  const limpo = texto.trim().replace(',', '.')
  if (limpo === '') return { acao: 'recusar', aviso: 'Digite a quantidade.' }
  const nova = Number(limpo)
  if (!Number.isFinite(nova)) return { acao: 'recusar', aviso: 'Quantidade inválida.' }
  if (nova < 0) return { acao: 'recusar', aviso: 'A quantidade não pode ser negativa.' }
  // So e "tira isso" quando a linha inteira fica em zero. Com a outra dimensao
  // viva, o zero e a propria venda: 0 caixas e 6 unidades soltas.
  if (nova === 0) return outraDimensao > 0 ? { acao: 'quantidade', nova: 0 } : { acao: 'remover' }
  if (nova === quantidadeAtual) return { acao: 'recusar', aviso: 'A quantidade é a mesma.' }
  return { acao: 'quantidade', nova }
}

// O gemeo de recalcularTotais que NAO grava: responde "quanto este sub-pedido
// vai ficar se eu aplicar esta mudanca?". E o que permite avisar sobre o pedido
// minimo ANTES de cortar, em vez de depois do estrago.
export function totalProjetado(
  itens: ItemPedido[],
  itemId: string,
  novaQuantidade: number,
): number {
  return arredondar2(
    itens.reduce((soma, item) => {
      if (item.id !== itemId) return soma + item.total_item
      if (novaQuantidade <= 0) return soma // quantidade 0 = item fora
      // ⚠ As avulsas do item entram na projecao. Sem elas o aviso de pedido
      // minimo mente para MENOS e ela corta um item que ja estava no minimo.
      return soma + calcularTotalItem(
        novaQuantidade, item.preco_unitario, item.desconto_pct, avulsasDoItem(item),
      )
    }, 0),
  )
}

export async function atualizarItem(
  item: ItemPedido,
  mudancas: {
    quantidade?: number
    desconto_pct?: number
    preco_unitario?: number
    quantidade_avulsa?: number
    preco_avulso?: number
  },
  motivo?: string,
): Promise<void> {
  const subPedido = await banco.sub_pedidos.get(item.sub_pedido_id)
  if (!subPedido) return
  garantirEditavel(subPedido)

  // Em rascunho o pedido ainda esta sendo montado: ninguem perdeu venda, entao
  // nao ha motivo a pedir nem historico a guardar.
  const comprometido = subPedido.status !== 'rascunho'

  const atualizado: ItemPedido = {
    ...item,
    quantidade: mudancas.quantidade ?? item.quantidade,
    quantidade_avulsa: mudancas.quantidade_avulsa ?? item.quantidade_avulsa ?? 0,
    preco_avulso: mudancas.preco_avulso ?? item.preco_avulso ?? null,
    desconto_pct: mudancas.desconto_pct ?? item.desconto_pct,
    preco_unitario: mudancas.preco_unitario ?? item.preco_unitario,
    origem_preco: mudancas.preco_unitario !== undefined ? 'manual' : item.origem_preco,
  }
  atualizado.total_item = calcularTotalItem(
    atualizado.quantidade, atualizado.preco_unitario, atualizado.desconto_pct,
    avulsasDoItem(atualizado),
  )

  if (comprometido) {
    // A checagem mora aqui, e nao antes, para o TypeScript enxergar que `motivo`
    // e string deste ponto em diante. Nada foi gravado ainda.
    if (!motivo) throw new Error('Informe o motivo da alteração.')
    // Aumentar nao e corte da fabrica: e conserto de engano, e nao pode entrar
    // na metrica de venda perdida.
    // ⚠ Compara UNIDADES, nao caixas. Com duas dimensoes a contagem de caixas
    // pode CAIR enquanto a venda cresce ("1 caixa de 20" -> "40 avulsas"), e a
    // guarda velha aplaudiria isso como corte da fabrica.
    if (unidadesTotais(atualizado) > unidadesTotais(item) && motivo !== MOTIVO_CORRECAO) {
      throw new Error('Aumentar a quantidade só com o motivo "Correção minha".')
    }
    await registrarAjuste({
      sub_pedido_id: subPedido.id,
      escopo: 'item',
      item_pedido_id: item.id,
      produto_id: item.produto_id,
      quantidade_antes: item.quantidade,
      quantidade_depois: atualizado.quantidade,
      // Sem estas duas o historico leria "1 caixa -> 1 caixa" num corte que
      // tirou 4 unidades soltas: linha de ajuste que parece um nada.
      quantidade_avulsa_antes: avulsasDoItem(item).quantidade,
      quantidade_avulsa_depois: avulsasDoItem(atualizado).quantidade,
      valor_antes: item.total_item,
      valor_depois: atualizado.total_item,
      motivo,
    })
  }

  await gravarLocalEEnfileirar('itens_pedido', atualizado)
  await recalcularTotais(subPedido.pedido_id)
  // A comissao acompanha o item real. Em rascunho nao ha lancamento ainda.
  if (comprometido) await sincronizarLancamento(subPedido.id)
}

export async function removerItem(item: ItemPedido, motivo?: string): Promise<void> {
  const subPedido = await banco.sub_pedidos.get(item.sub_pedido_id)
  if (!subPedido) return
  garantirEditavel(subPedido)
  if (subPedido.status === 'rascunho') {
    // rascunho: comportamento de sempre -- ninguem perdeu venda, carrinho vazio some
    await apagarLocalEEnfileirar('itens_pedido', item.id)
    if ((await itensAtivos(subPedido.id)).length === 0) {
      await apagarLocalEEnfileirar('sub_pedidos', subPedido.id)
    }
    await recalcularTotais(subPedido.pedido_id)
    return
  }

  // Daqui pra baixo o sub-pedido ja foi comprometido com o cliente. A checagem
  // vem ANTES de apagar: recusar depois de remover deixaria o item perdido.
  if (!motivo) throw new Error('Informe o motivo da alteração.')

  await apagarLocalEEnfileirar('itens_pedido', item.id)
  const restantes = await itensAtivos(subPedido.id)

  if (restantes.length === 0) {
    // Ultimo item de um sub-pedido ja comprometido: ele tem letra, numero, PDF
    // na fabrica e lancamento de comissao. Apagar destruiria tudo isso -- entao
    // CANCELA, com o mesmo motivo, e registra a perda do sub-pedido inteiro.
    await registrarAjuste({
      sub_pedido_id: subPedido.id,
      escopo: 'sub_pedido',
      item_pedido_id: null,
      produto_id: null,
      quantidade_antes: null,
      quantidade_depois: null,
      valor_antes: subPedido.valor_total,
      valor_depois: 0,
      motivo,
    })
    await recalcularTotais(subPedido.pedido_id)
    // Reler ANTES de mudar o status: mudarStatusSubPedido grava o objeto
    // inteiro, e o `subPedido` em maos ainda tem o valor_total de antes do
    // recalculo -- usa-lo aqui ressuscitaria o total velho.
    const atual = await banco.sub_pedidos.get(subPedido.id)
    if (atual) {
      await mudarStatusSubPedido(atual, 'cancelado', { motivo_cancelamento: motivo })
    }
    return
  }

  await registrarAjuste({
    sub_pedido_id: subPedido.id,
    escopo: 'item',
    item_pedido_id: item.id,
    produto_id: item.produto_id,
    quantidade_antes: item.quantidade,
    quantidade_depois: 0,
    valor_antes: item.total_item,
    valor_depois: 0,
    motivo,
  })
  await recalcularTotais(subPedido.pedido_id)
  await sincronizarLancamento(subPedido.id)
}

// A porta da DEVOLUCAO (Ciclo 2). De proposito nao passa por garantirEditavel:
// item de sub-pedido faturado e congelado para EDICAO, e devolucao nao e edicao
// -- e o registro de que a mercadoria voltou. Por isso tem guarda propria, ao
// contrario (exige faturado, onde a edicao termina) e lista de motivo propria.
//
// `quantidadeDevolvida` omitida = voltou tudo. Observacao dela no smoke de
// 2026-07-30: "nem sempre e devolvida toda a quantidade daquele produto" --
// voltar parte REDUZ o item, e so voltar tudo e que o tira do pedido.
export async function devolverItem(
  item: ItemPedido,
  motivo: string,
  quantidadeDevolvida: number = item.quantidade,
): Promise<void> {
  const subPedido = await banco.sub_pedidos.get(item.sub_pedido_id)
  if (!subPedido) return
  // A devolucao acontece na CONFERENCIA da entrega, antes de confirma-la: por
  // isso so em faturado. Depois de "entregue" ela ja disse que estava tudo la.
  if (subPedido.status !== 'faturado') {
    throw new Error(
      `Devolução só na conferência da entrega: o sub-pedido ${subPedido.letra} está "${subPedido.status}", não faturado.`,
    )
  }
  // Antes de apagar, como no corte: recusar depois de remover perderia o item.
  if (!motivo) throw new Error('Informe o motivo da devolução.')
  if (!ehMotivoDeDevolucao(motivo)) {
    throw new Error(
      'Motivo de corte não serve para devolução: use "Devolução de item pelo cliente", ' +
        '"Avaria na entrega" ou "Não achou o endereço".',
    )
  }
  // Devolver mais do que saiu seria inventar mercadoria; zero nao e devolucao.
  if (!Number.isFinite(quantidadeDevolvida) || quantidadeDevolvida <= 0) {
    throw new Error('Informe a quantidade devolvida (maior que zero).')
  }
  if (quantidadeDevolvida > item.quantidade) {
    throw new Error(
      `Quantidade devolvida maior do que a vendida: saíram ${item.quantidade} un deste produto.`,
    )
  }

  const restante = item.quantidade - quantidadeDevolvida
  if (restante > 0) {
    // Voltou PARTE: o item continua no pedido, com menos unidades. Nada de
    // apagar e recriar -- o historico aponta para este mesmo item_pedido_id.
    const atualizado: ItemPedido = {
      ...item,
      quantidade: restante,
      total_item: calcularTotalItem(restante, item.preco_unitario, item.desconto_pct),
    }
    await gravarLocalEEnfileirar('itens_pedido', atualizado)
    await registrarAjuste({
      sub_pedido_id: subPedido.id,
      escopo: 'item',
      item_pedido_id: item.id,
      produto_id: item.produto_id,
      quantidade_antes: item.quantidade,
      quantidade_depois: restante,
      valor_antes: item.total_item,
      valor_depois: atualizado.total_item,
      motivo,
    })
    await recalcularTotais(subPedido.pedido_id)
    await sincronizarLancamento(subPedido.id)
    return
  }

  await apagarLocalEEnfileirar('itens_pedido', item.id)
  // A linha do item vem sempre, inclusive quando e o ultimo -- diferente do
  // corte do Ciclo 1. Aqui o QUE voltou importa: e mercadoria fisica de volta.
  await registrarAjuste({
    sub_pedido_id: subPedido.id,
    escopo: 'item',
    item_pedido_id: item.id,
    produto_id: item.produto_id,
    quantidade_antes: item.quantidade,
    quantidade_depois: 0,
    valor_antes: item.total_item,
    valor_depois: 0,
    motivo,
  })
  await recalcularTotais(subPedido.pedido_id)

  if ((await itensAtivos(subPedido.id)).length === 0) {
    // Voltou tudo: nao pode sobrar pedido faturado e vazio. Cancela em vez de
    // apagar, pelo mesmo motivo do Ciclo 1 (letra, numero, PDF, comissao).
    await registrarAjuste({
      sub_pedido_id: subPedido.id,
      escopo: 'sub_pedido',
      item_pedido_id: null,
      produto_id: null,
      quantidade_antes: null,
      quantidade_depois: null,
      valor_antes: subPedido.valor_total,
      valor_depois: 0,
      motivo,
    })
    // Reler ANTES: mudarStatusSubPedido grava o objeto inteiro, e o que esta em
    // maos tem o valor_total de antes do recalculo.
    const atual = await banco.sub_pedidos.get(subPedido.id)
    if (atual) await mudarStatusSubPedido(atual, 'cancelado', { motivo_cancelamento: motivo })
    return
  }
  // Sobrou item: o sub-pedido segue FATURADO (devolucao e operacao de item, nao
  // transicao de estado) e a comissao acompanha o total novo.
  await sincronizarLancamento(subPedido.id)
}

// ▶ Pedido dela 14/10: com o pedido ainda em RASCUNHO, corrigir o cadastro do
// produto (preco da tabela, embalagem) tem de refletir no pedido -- o item
// guarda uma COPIA do preco e do fator, e antes disto ela ficava velha para
// sempre. So rascunho: a partir do fechamento a venda congela, e um pedido ja
// enviado nunca muda sozinho (a spec de 28/08 proibe reescrever PDF antigo).
//
// Poupa o que e decisao da venda: preco digitado a mao (origem 'manual'),
// desconto e quantidades. Item com unidades avulsas so e tocado se o produto
// ainda vende avulso -- apagar em silencio uma parcela que o vendedor digitou
// seria pior do que deixar o item como esta.
//
// Devolve quantos itens mudaram. Chamada ao abrir o pedido; sem mudanca, nao
// grava nada (nao enche a fila de sincronizacao a toa).
export async function atualizarItensDoRascunho(
  pedidoId: string,
  clienteId: string,
): Promise<number> {
  let mudaram = 0
  for (const subPedido of await subPedidosAtivos(pedidoId)) {
    if (subPedido.status !== 'rascunho') continue
    for (const item of await itensAtivos(subPedido.id)) {
      if (item.origem_preco === 'manual') continue
      const produto = await banco.produtos.get(item.produto_id)
      if (!produto || produto.apagado_em !== null) continue
      const resolvido = await resolverPreco(item.produto_id, clienteId, subPedido.tabela_preco_id)
      if (!resolvido) continue

      const fator = embalagemDoProduto(produto).fator
      const tinhaAvulsas = (item.quantidade_avulsa ?? 0) > 0
      if (tinhaAvulsas && (fator === null || resolvido.precoUnidade === null)) continue

      const precoAvulso = tinhaAvulsas ? resolvido.precoUnidade : (item.preco_avulso ?? null)
      const composicao = await comporImpostoInformativo(
        produto, clienteId, resolvido.preco, subPedido.frete_tipo, subPedido.condicao_pagamento_id,
      )
      const atualizado: ItemPedido = {
        ...item,
        preco_unitario: resolvido.preco,
        preco_avulso: precoAvulso,
        unidades_por_embalagem: fator,
        origem_preco: resolvido.origem,
        total_item: calcularTotalItem(
          item.quantidade, resolvido.preco, item.desconto_pct, avulsasDoItem({ ...item, preco_avulso: precoAvulso }),
        ),
        imposto_origem: composicao?.origem ?? null,
        imposto_destino_icms: composicao?.destinoIcms ?? null,
        imposto_diferenca_icms: composicao?.diferencaIcms ?? null,
        imposto_ipi: composicao?.ipi ?? null,
      }
      const igual =
        atualizado.preco_unitario === item.preco_unitario
        && atualizado.preco_avulso === (item.preco_avulso ?? null)
        && atualizado.unidades_por_embalagem === (item.unidades_por_embalagem ?? null)
        && atualizado.origem_preco === item.origem_preco
        && atualizado.imposto_ipi === (item.imposto_ipi ?? null)
        && atualizado.imposto_diferenca_icms === (item.imposto_diferenca_icms ?? null)
      if (igual) continue
      await gravarLocalEEnfileirar('itens_pedido', atualizado)
      mudaram++
    }
  }
  if (mudaram > 0) await recalcularTotais(pedidoId)
  return mudaram
}
